import { type InfiniteData, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'

import type { NotificationFilter } from '@fv/models'

import { type Notification, notificationKeys } from './queries'

/*
Maintains the single notification cache
*/
export const useUpdateNotificationCache = () => {
  const queryClient = useQueryClient()
  return (notification: Notification) => {
    queryClient.setQueryData(
      notificationKeys.byId(notification._id),
      notification,
    )
  }
}

/*
Maintains the notification list cache
*/
type NotificationsQueryData = InfiniteData<{ data: Notification[] }>
export const useUpdateNotificationsInCache = () => {
  const queryClient = useQueryClient()
  return (
    notificationsModifier: (notifications: Notification[]) => Notification[],
  ) => {
    queryClient.setQueriesData<NotificationsQueryData>(
      {
        queryKey: notificationKeys.list(),
      },
      data => {
        if (!data) return data
        return {
          ...data,
          pages: data.pages.map(p => ({
            ...p,
            data: notificationsModifier(p.data),
          })),
        }
      },
    )
  }
}

/*
Helper method to maintain a notification's cache in both lists & by id
*/
export const useUpdateNotificationInCaches = () => {
  const notificationCacheHelper = useUpdateNotificationCache()
  const notificationsListHelper = useUpdateNotificationsInCache()
  return (updatedNotification: Notification) => {
    notificationCacheHelper(updatedNotification)
    notificationsListHelper(data =>
      data.map(n =>
        n._id === updatedNotification._id
          ? {
              ...n,
              ...updatedNotification,
            }
          : n,
      ),
    )
  }
}

export const useCountCacheModifier = (filter: NotificationFilter) => {
  // const invalidateUnreadCount = useUnreadCountCacheInvalidator()
  const queryClient = useQueryClient()
  return (inc: number) => {
    queryClient.setQueryData<number>(
      notificationKeys.filteredCount(filter),
      data => {
        if (data === undefined) return data
        if (data === 0 && inc < 0) return data
        return data + inc
      },
    )
    // invalidateUnreadCount(filter)
  }
}

export const useUnreadCountCacheInvalidator = (delay = 5000) => {
  const timeoutRef = useRef<NodeJS.Timeout>(null)
  const queryClient = useQueryClient()
  return (filter: NotificationFilter) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: notificationKeys.filteredCount(filter),
      })
    }, delay)
  }
}
